import intl from 'react-intl-universal';
import moment from 'moment-timezone';
import { Fragment } from 'react';
import Grid from '@mui/material/Grid2';
import { SimpleTreeView, TreeItem, treeItemClasses } from '@mui/x-tree-view';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Chip from '../components/Chip';
import Icon from '@mui/material/Icon';
import Stack from '@mui/material/Stack';
import DeleteIcon from '@mui/icons-material/Delete';
import localData from 'utils/localData';
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import { Box, IconButton } from '@mui/material';
import api from 'utils/api';
import { formatBytes } from 'utils/misc';
import { FILE_CHUNK_SIZE, uploadFile } from './S3FileUpload';
import {
  setUploadingFileNameContext,
  setUploadControllerContext,
  updateMissionInfoContext,
} from 'context/NewIssueContext';

import {
  getStructuredFiles,
  flattenStructuredAttachmentsObject,
} from 'utils/fileHandling';
import { FILE_TYPE } from 'shared/constants/file';
import { LANGUAGE, USER_DATA } from 'shared/constants/users';
import { DOLPHIN_API, KENSHIN_API } from 'shared/constants/apis';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import logger from 'utils/logger';
import { MISSION_STATUS } from 'shared/constants/uploadAttachments';
import {
  GENDER_OPTIONS,
  createFormData,
} from 'src/components/UploadMissions/util';

export const BACKEND_DESTINATION = {
  DOLPHIN: 'dolphin',
  KENSHIN: 'kenshin',
};

export const EMERGENCY_STATUS = {
  REGULAR: 1,
  TODAY: 3,
  URGENT: 5,
  IN_THREE_DAYS: 6,
};

const StyledTreeItem = styled(TreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.label}`]: {
    fontSize: '1rem !important',
    fontWeight: '200 !important',
    color: theme.palette.text.main,
  },
}));

const log = logger.createLogger('NewIssueUtils');

const consoleLogApiError = (error) => {
  if (error.response) {
    // The server responded with a status code out of the range of 2xx;
    console.log(`error.response: ${JSON.stringify(error.response)}`);
  } else if (error.request) {
    // The request was made but no response was received;
    // `error.request` is an XMLHttpRequest instance in the browser;
    console.log(
      `request made, no response received; error.request: ${JSON.stringify(
        error.request
      )}`
    );
    throw new Error('Network Failure');
  } else {
    // Something happened in setting up the request that triggered an Error
    console.log('Unknown Error', error.message);
  }
};

export const getGenderOptions = () => {
  return [
    {
      label: intl.get('new_option_patient_gender_female'),
      value: GENDER_OPTIONS.FEMALE,
    },
    {
      label: intl.get('new_option_patient_gender_male'),
      value: GENDER_OPTIONS.MALE,
    },
    {
      label: intl.get('new_option_patient_gender_others'),
      value: GENDER_OPTIONS.OTHERS,
    },
  ];
};

export const getGenderValue = (genderValue) => {
  if (genderValue === GENDER_OPTIONS.FEMALE) {
    return intl.get('new_option_patient_gender_female');
  } else if (genderValue === GENDER_OPTIONS.MALE) {
    return intl.get('new_option_patient_gender_male');
  } else if (genderValue === GENDER_OPTIONS.OTHERS) {
    return intl.get('new_option_patient_gender_others');
  }
};

export const getEmergencyOptions = (type = BACKEND_DESTINATION.DOLPHIN) => {
  if (type === BACKEND_DESTINATION.DOLPHIN) {
    return [
      {
        label: intl.get('new_option_emergency_level_1'),
        value: EMERGENCY_STATUS.REGULAR,
      },
      {
        label: intl.get('new_option_emergency_level_3'),
        value: EMERGENCY_STATUS.TODAY,
      },
      {
        label: intl.get('new_option_emergency_level_5'),
        value: EMERGENCY_STATUS.URGENT,
      },
      {
        label: intl.get('new_option_emergency_in_3_days'),
        value: EMERGENCY_STATUS.IN_THREE_DAYS,
      },
    ];
  } else {
    return [
      {
        label: intl.get('new_option_emergency_level_1'),
        value: EMERGENCY_STATUS.REGULAR,
      },
    ];
  }
};

export const getDstOrgOptions = (dstOrgs) => {
  return dstOrgs.map((dstOrg) => {
    return {
      label: `${dstOrg.name}`,
      value: dstOrg.id,
    };
  });
};

export const getDstOrgObjectById = (orgId, dstOrgs) => {
  const res = dstOrgs.filter((org) => org.id.toString() === orgId.toString());
  if (res.length > 0) {
    return res[0];
  }
};

export const renderSelectedTagsChips = (selectedTagIds, tagList, darkMode) => {
  const languageCode = localData.get(USER_DATA.PREFFERED_LANGUAGE);
  if (!selectedTagIds || selectedTagIds.length === 0) {
    return null;
  }
  const renderedChips = selectedTagIds.map((tagId, index) => {
    const tag = tagList.find((tag) => tag.id === tagId);
    const labelText =
      tag.displayName[languageCode] ||
      tag.displayName[LANGUAGE.ENGLISH] ||
      Object.values(tag.displayName)[0];

    return (
      <Chip
        data-testid="new-issue-tags-selected-chip"
        key={index}
        label={labelText}
        labelcolor={darkMode ? 'white' : 'black'}
        variant="outlined"
        style={{ marginRight: '4px' }}
      />
    );
  });

  return (
    <Fragment>
      <Grid size={12}>
        <MDTypography style={{ fontWeight: 'bold' }}>
          {intl.get('new_modal_confirm_text_tags')}
        </MDTypography>
      </Grid>
      <Grid size={12}>{renderedChips}</Grid>
    </Fragment>
  );
};

export const generateConfirmWindowContents = (
  formState,
  structuredAttachments,
  darkMode,
  selectedTagIds,
  tagList,
  isKenshin = false
) => {
  const getFilesList = () =>
    structuredAttachments.map((item, index) => {
      return createFileItem(item, index);
    });

  const createFileItem = (item, index) => {
    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          width: '100%',
        }}>
        <Icon fontSize="large" color="secondary" sx={{ mr: 2, mt: 0.5 }}>
          {item.type === 'directory' ? 'folder' : 'attachment'}
        </Icon>
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <Stack spacing={1}>
            <MDTypography
              variant="body2"
              sx={{ maxWidth: '100%', wordBreak: 'break-word' }}>
              {intl.get('new_label_file_name')}: {item.name}
            </MDTypography>
            <MDTypography variant="body2">
              {intl.get('new_label_file_size')}: {formatBytes(item.size)}{' '}
            </MDTypography>
          </Stack>
        </Box>
      </Box>
    );
  };

  // TODO This is duplicated code from the generateMissionDetailsModalContents function in the UploadMissions component
  return (
    <Grid
      spacing={1}
      container
      sx={{
        marginTop: '15px',
        overflowY: 'auto',
        overflowx: 'hidden',
        maxHeight: '40vh',
      }}>
      {createFormData(formState, isKenshin)}
      {renderSelectedTagsChips(selectedTagIds, tagList, darkMode)}
      {getFilesList()}
    </Grid>
  );
};

export const generateFileList = (structuredAttachments, handleFileDelete) => {
  return structuredAttachments
    .filter((file) => file.type === FILE_TYPE.FILE)
    .map((item, index) => (
      <MDBox style={{ marginBottom: '15px' }} key={index}>
        <Grid container spacing={1} alignItems="center">
          <Grid>
            <IconButton onClick={handleFileDelete(item)}>
              <DeleteIcon />
            </IconButton>
          </Grid>
          <Grid>
            <MDTypography variant="body2" data-testid={`attachment-file`}>
              {item.name} - {formatBytes(item.size)}
            </MDTypography>
          </Grid>
        </Grid>
      </MDBox>
    ));
};

export const generateDirectoryList = (
  structuredAttachments,
  handleFileDelete
) => {
  return structuredAttachments.map((item, index) => {
    if (item.type === FILE_TYPE.DIRECTORY) {
      return (
        <Grid container spacing={1} key={index}>
          <Grid size={11} style={{ display: 'flex' }}>
            <FileCopyIcon sx={{ mt: 0.7, mr: 1 }} />
            <SimpleTreeView
              sx={{
                height: '100%',
                overflowY: 'auto',
              }}
              slots={{
                collapseIcon: ExpandMoreIcon,
                expandIcon: ChevronRightIcon,
              }}>
              {generateTreeView(item, '')}
            </SimpleTreeView>
          </Grid>
          <Grid>
            <IconButton
              onClick={handleFileDelete(item)}
              sx={(theme) => {
                return { color: theme.palette.text.main };
              }}>
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      );
    } else {
      return (
        <Grid container spacing={1} key={index}>
          <Grid size={11} style={{ display: 'flex', alignItems: 'center' }}>
            <InsertDriveFileIcon />
            <MDTypography
              sx={{ pl: 1 }}
              variant="body2"
              data-testid="attachment-file">
              {item.name} - {formatBytes(item.size)}
            </MDTypography>
          </Grid>
          <Grid>
            <IconButton
              onClick={handleFileDelete(item)}
              sx={(theme) => {
                return { color: theme.palette.text.main };
              }}>
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      );
    }
  });
};

export const generateFileListForAttachmentsFromParent = (
  attachmentsFromParent,
  handleAttachmentDelete
) => {
  return attachmentsFromParent
    .filter((attachment) => attachment.structureJson.type === FILE_TYPE.FILE)
    .map((item, index) => (
      <MDBox style={{ marginBottom: '15px' }} key={index}>
        <Grid container spacing={1} alignItems="center">
          <Grid>
            <IconButton onClick={handleAttachmentDelete(item)}>
              <DeleteIcon />
            </IconButton>
          </Grid>
          <Grid>
            <MDTypography variant="body2">
              {item.structureJson.name} - {formatBytes(item.structureJson.size)}
            </MDTypography>
          </Grid>
        </Grid>
      </MDBox>
    ));
};

export const generateDirectoryListForAttachmentsFromParent = (
  attachmentsFromParent,
  handleAttachmentDelete,
  darkMode
) => {
  return attachmentsFromParent
    .filter(
      (attachment) => attachment.structureJson.type === FILE_TYPE.DIRECTORY
    )
    .map((item, index) => (
      <MDBox style={{ marginBottom: '15px' }} key={index}>
        <Grid container spacing={3} alignItems="center">
          <Grid>
            <IconButton onClick={handleAttachmentDelete(item)}>
              <DeleteIcon />
            </IconButton>
          </Grid>
          <Grid>
            {item.structureJson.type === 'file' ? (
              <MDTypography variant="body2">
                {item.structureJson.name}-{formatBytes(item.structureJson.size)}
              </MDTypography>
            ) : (
              <SimpleTreeView
                expandIcon={<ChevronRightIcon />}
                collapseIcon={<ExpandMoreIcon />}
                style={{
                  color: darkMode ? 'white' : 'black',
                  backgroundColor: darkMode ? '#202940' : 'white',
                }}
                sx={{
                  maxheight: 400,
                  flexGrow: 1,
                  maxWidth: 500,
                  overflowY: 'auto',
                }}>
                {generateTreeView(item.structureJson, index)}
              </SimpleTreeView>
            )}
          </Grid>
        </Grid>
      </MDBox>
    ));
};

const generateTreeView = (structureObject, index) => {
  let size = '';
  if (structureObject.size && structureObject.size > 0) {
    size = formatBytes(structureObject.size);
  }
  const label =
    size.length > 0
      ? `${structureObject.name} - ${size}`
      : `${structureObject.name}`;

  const itemId = structureObject.fileFullPath
    ? structureObject.fileFullPath
    : `${structureObject.name} - ${size}`;

  return (
    <StyledTreeItem key={index} itemId={itemId} label={label}>
      {structureObject.type === FILE_TYPE.DIRECTORY &&
        structureObject.children.map((item, childIndex) =>
          generateTreeView(item, `${index}-${childIndex}`)
        )}
    </StyledTreeItem>
  );
};

export const initNewKenshinIssue = async (
  formState,
  attachmentsFromParent,
  structuredAttachments,
  selectedTagIds
) => {
  // POST to backend server to create a new Issue Entity;
  const currentOrganizationId = localData.get(
    USER_DATA.CURRENT_ORGANIZATION_ID
  );
  const formData = {};
  formData.description = formState.description;
  formData.emergency = formState.emergency;
  formData.department = formState.department;
  formData.srcOrgName = formState.srcOrgName;
  formData.srcOrgId = formState.srcOrgId;
  formData.dstOrgName = formState.dstOrgName;
  formData.dstOrgId = formState.dstOrgId;
  formData.numAttachments = structuredAttachments.length;
  formData.selectedTagIds = selectedTagIds;

  // For attachments from parent
  formData.attachmentFromParentIds = attachmentsFromParent.map((item) => {
    return item.id;
  });
  formData.parentIssueId = formState.parentIssueId;

  const apiVariables = {
    params: {
      organizationId: currentOrganizationId,
      username: localData.get(USER_DATA.USERNAME),
      userId: localData.get(USER_DATA.USER_ID),
    },
    data: formData,
    baseURL: import.meta.env.VITE_KENSHIN_API_URL,
  };

  let issueInfo;
  try {
    issueInfo = await api.post(`${KENSHIN_API.ISSUES}/new`, apiVariables);
  } catch (error) {
    log.log(`POST to /api/issues/new Failed`);
    consoleLogApiError(error);
    throw error;
  }
  return issueInfo;
};

export const initNewIssue = async (
  formState,
  attachmentsFromParent,
  structuredAttachments,
  selectedTagIds,
  destination = BACKEND_DESTINATION.DOLPHIN
) => {
  // POST to backend server to create a new Issue Entity;
  const currentOrganizationId = localData.get(
    USER_DATA.CURRENT_ORGANIZATION_ID
  );
  let apiVariables;
  if (destination === BACKEND_DESTINATION.DOLPHIN) {
    const formData = {};
    formData.description = formState.description;
    formData.emergency = formState.emergency;
    formData.patientGender = formState.patientGender;
    formData.patientName = formState.patientName;
    formData.department = formState.department;
    formData.doctorInCharge = formState.doctorInCharge;
    formData.doctorsInCharge = formState.doctorsInCharge;
    formData.doctorIdsFromSrcOrg = formState.doctorIdsFromSrcOrg;
    formData.srcOrgName = formState.srcOrgName;
    formData.srcOrgId = parseInt(formState.srcOrgId);
    formData.dstOrgName = formState.dstOrgName;
    formData.dstOrgId = parseInt(formState.dstOrgId);
    formData.numAttachments = structuredAttachments.length;
    formData.selectedTagIds = selectedTagIds;

    // For attachments from parent
    formData.attachmentFromParentIds = attachmentsFromParent.map((item) => {
      return item.id;
    });
    formData.parentIssueId = formState.parentIssueId;

    apiVariables = {
      params: { organizationId: currentOrganizationId },
      data: formData,
    };
  } else {
    const formData = {};
    formData.description = formState.description;
    formData.emergency = formState.emergency;
    formData.department = formState.department;
    formData.srcOrgName = formState.srcOrgName;
    formData.srcOrgId = parseInt(formState.srcOrgId);
    formData.dstOrgName = formState.dstOrgName;
    formData.dstOrgId = parseInt(formState.dstOrgId);
    formData.numAttachments = structuredAttachments.length;
    formData.selectedTagIds = selectedTagIds;

    apiVariables = {
      params: {
        organizationId: currentOrganizationId,
        username: localData.get(USER_DATA.USERNAME),
        userId: localData.get(USER_DATA.USER_ID),
      },
      data: formData,
      baseURL: import.meta.env.VITE_KENSHIN_API_URL,
    };
  }

  let issueInfo;
  try {
    issueInfo = await api.post(
      `${
        destination === BACKEND_DESTINATION.DOLPHIN
          ? DOLPHIN_API.ISSUES
          : KENSHIN_API.ISSUES
      }/new`,
      apiVariables
    );
  } catch (error) {
    log.error(`POST to /api/issues/new Failed`);
    consoleLogApiError(error);
    throw error;
  }
  return issueInfo;
};

export const startIssuePeriodicStatusCheck = async (
  issueId,
  destination = BACKEND_DESTINATION.DOLPHIN
) => {
  let status;
  try {
    if (destination === BACKEND_DESTINATION.DOLPHIN) {
      const apiVariables = {
        params: {
          organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
          issueId: issueId,
        },
      };
      const res = await api.get(
        `${DOLPHIN_API.ISSUES}/periodic-status-check`,
        apiVariables
      );
      status = res.status;
    } else {
      const apiVariables = {
        params: {
          organizationId: parseInt(
            localData.get(USER_DATA.CURRENT_ORGANIZATION_ID)
          ),
          issueId: parseInt(issueId),
          username: localData.get(USER_DATA.USERNAME),
          userId: parseInt(localData.get(USER_DATA.USER_ID)),
        },
        baseURL: import.meta.env.VITE_KENSHIN_API_URL,
      };
      const res = await api.get(
        `${KENSHIN_API.ISSUES}/periodic-status-check`,
        apiVariables
      );
      status = res.status;
    }
  } catch (error) {
    log.error(
      `Request to /api/attachment/periodic-status-check failed; Check network condition;`
    );
    consoleLogApiError(error);
  }

  return status;
};

/**
 *  Get dateTime string in given format (Japan)
 */
const getTimeStringInFormat = (issueCreatedAt) => {
  const format = 'YYYY-MM-DD-HH-mm';
  const dateTimeString = moment(issueCreatedAt).tz('Asia/Tokyo').format(format);
  return dateTimeString;
};

const notifyServerAboutAttachment = async ({
  issueId,
  issueIdentifierId,
  issueDateTimeString,
  updatedAttachmentItem,
  destination = BACKEND_DESTINATION.DOLPHIN,
}) => {
  try {
    if (destination === BACKEND_DESTINATION.DOLPHIN) {
      const apiVariables = {
        params: {
          organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        },
        data: {
          issueId: issueId,
          issueIdentifierId: issueIdentifierId,
          issueDateTimeString: issueDateTimeString,
          attachmentObj: updatedAttachmentItem,
        },
      };
      const res = await api.post(
        `${DOLPHIN_API.ATTACHMENT}/notify-upload-completion`,
        apiVariables
      );
      return res;
    } else {
      const apiVariables = {
        params: {
          organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
          username: localData.get(USER_DATA.USERNAME),
          userId: localData.get(USER_DATA.USER_ID),
        },
        data: {
          issueId: issueId,
          issueIdentifierId: issueIdentifierId,
          issueDateTimeString: issueDateTimeString,
          attachmentObj: updatedAttachmentItem,
        },
        baseURL: import.meta.env.VITE_KENSHIN_API_URL,
      };

      const res = await api.post(
        `${KENSHIN_API.ATTACHMENT}/notify-upload-completion`,
        apiVariables
      );
      return res;
    }
  } catch (error) {
    log.error(`POST to /api/attachment/notify-upload-completion Failed`);
    consoleLogApiError(error);
    throw error;
  }
};

export const checkForAttachmentsCompletionOnNewIssue = async (
  issueId,
  destination = BACKEND_DESTINATION.DOLPHIN
) => {
  let status;
  try {
    if (destination === BACKEND_DESTINATION.DOLPHIN) {
      const apiVariables = {
        params: {
          organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
          issueId: issueId,
        },
      };
      const res = await api.get(
        `${DOLPHIN_API.ATTACHMENT}/check-for-attachments-completion-on-new-issue`,
        apiVariables
      );
      status = res.status;
    } else {
      const apiVariables = {
        params: {
          organizationId: parseInt(
            localData.get(USER_DATA.CURRENT_ORGANIZATION_ID)
          ),
          issueId: parseInt(issueId),
          username: localData.get(USER_DATA.USERNAME),
          userId: localData.get(USER_DATA.USER_ID),
        },
        baseURL: import.meta.env.VITE_KENSHIN_API_URL,
      };
      const res = await api.get(
        `${KENSHIN_API.ATTACHMENT}/check-for-attachments-completion-on-new-issue`,
        apiVariables
      );
      status = res.status;
    }
  } catch (error) {
    log.error(
      `Request to /api/attachment/check-for-attachments-completion-on-new-issue failed; Check network condition;`
    );
    consoleLogApiError(error);
  }

  return status;
};

export const uploadBatchAttachmentsToS3 = async ({
  missionId,
  issueId,
  issueIdentifierId,
  issueCreatedAt,
  structuredAttachments,
  batchTotalBytes,
  srcOrgId,
  dstOrgId,
  controllerUploadMissions,
  dispatchUploadMissions,
  destination = BACKEND_DESTINATION.DOLPHIN,
}) => {
  const issueDateTimeString = getTimeStringInFormat(issueCreatedAt);

  const completedUploads = [];
  const failedUploads = [];
  const attachments = [];

  // For each attachment: upload and notify;
  for (var i = 0; i < structuredAttachments.length; i++) {
    const AttachmentItem = structuredAttachments[i];

    try {
      // 1. Upload attachment into S3 bucket; no need to be non-blocking / async;
      const updatedAttachmentItem = await uploadSingleAttachmentToS3({
        missionId,
        issueIdentifierId,
        issueCreatedAt,
        issueDateTimeString,
        AttachmentItem,
        batchTotalBytes,
        srcOrgId,
        dstOrgId,
        controllerUploadMissions,
        dispatchUploadMissions,
        destination,
      });

      //! ERROR in this API call
      // 2. Notify backend that it is in s3, ready for processing;
      const res = await notifyServerAboutAttachment({
        issueId,
        issueIdentifierId,
        issueDateTimeString,
        updatedAttachmentItem,
        destination,
      });

      completedUploads.push({
        name: AttachmentItem.name,
        file: AttachmentItem,
        response: res,
      });

      attachments.push(res);
    } catch (error) {
      if (error.code === MISSION_STATUS.ERR_CANCELED) {
        log.warn(`Upload is aborted: ${error.message}`);
        throw error;
      } else {
        log.error(`Uploading attachment ${AttachmentItem.name} failed`);
        failedUploads.push({
          name: AttachmentItem.name,
          error: error.message || 'Upload failed',
          file: AttachmentItem,
        });
      }
    }
    updateMissionInfoContext(dispatchUploadMissions, {
      missionId,
      failedUploads,
      completedUploads,
    });
  }
  return attachments;
};

export const uploadSingleAttachmentToS3 = async ({
  missionId,
  issueIdentifierId,
  issueCreatedAt,
  issueDateTimeString,
  AttachmentItem,
  batchTotalBytes,
  srcOrgId,
  dstOrgId,
  controllerUploadMissions,
  dispatchUploadMissions,
  destination,
}) => {
  // flatten the attachment object into an array of objects, each representing a single file;
  let arrayOfFileObjects = flattenStructuredAttachmentsObject(AttachmentItem);

  for (var i = 0; i < arrayOfFileObjects.length; i++) {
    const item = arrayOfFileObjects[i];
    setUploadingFileNameContext(dispatchUploadMissions, {
      missionId,
      value: item.fileFullPath,
    });
    const axiosUploadController = new AbortController();
    setUploadControllerContext(dispatchUploadMissions, {
      missionId,
      value: axiosUploadController,
    });

    const objectInfo = await uploadSingleFileToS3({
      missionId,
      issueIdentifierId,
      issueCreatedAt,
      issueDateTimeString,
      batchTotalBytes,
      srcOrgId,
      dstOrgId,
      attachmentFileObj: item,
      controllerUploadMissions,
      dispatchUploadMissions,
      axiosUploadController,
      destination,
    });

    // TODO Need to show error on UI here

    arrayOfFileObjects[i] = { ...item, ...objectInfo }; // add new info to each object
  }

  const arrayOfUpdatedStructuredAttachment =
    getStructuredFiles(arrayOfFileObjects);
  const updatedStructuredAttachment = arrayOfUpdatedStructuredAttachment[0]; // logically, the array can not be empty if the parent function is triggered;
  return updatedStructuredAttachment;
};

/**
 *  Upload the file to S3 bucket through pre-signed URL
 */
export const uploadSingleFileToS3 = async ({
  missionId,
  issueIdentifierId,
  issueCreatedAt,
  issueDateTimeString,
  batchTotalBytes,
  srcOrgId,
  dstOrgId,
  attachmentFileObj, // an object of one single file
  controllerUploadMissions,
  dispatchUploadMissions,
  axiosUploadController,
  destination = BACKEND_DESTINATION.DOLPHIN,
}) => {
  const { file, fileFullPath } = attachmentFileObj;
  let bucketName;
  let objectKey;
  try {
    // use S3 Multipart Upload if file is larger than threshold;
    const multipart =
      attachmentFileObj.size > FILE_CHUNK_SIZE
        ? Math.ceil(attachmentFileObj.size / FILE_CHUNK_SIZE)
        : 0;

    let res;
    const baseParams = {
      multipart: multipart,
      organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
      srcOrgId: srcOrgId,
      dstOrgId: dstOrgId,
      filename: fileFullPath,
      issueIdentifierId,
      issueCreatedAt,
      issueDateTimeString,
    };

    const kenshinParams =
      destination !== BACKEND_DESTINATION.DOLPHIN
        ? {
            username: localData.get(USER_DATA.USERNAME),
            userId: localData.get(USER_DATA.USER_ID),
          }
        : {};

    const apiVariables = { params: { ...baseParams, ...kenshinParams } };

    const endpoint =
      destination === BACKEND_DESTINATION.DOLPHIN
        ? `${DOLPHIN_API.ISSUES}/url-for-uploading-file`
        : `${KENSHIN_API.ISSUES}/url-for-uploading-file`;

    res = await api.get(endpoint, apiVariables);

    const { presignedS3Url, uploadId } = res;

    bucketName = res.bucketName;
    objectKey = res.objectKey;

    if (multipart && import.meta.env.VITE_CLOUD_PROVIDER === 'aws') {
      // Multipart Upload
      const parts = await uploadFile({
        file,
        presignedS3Url,
        batchTotalBytes,
        missionId,
        controllerUploadMissions,
        dispatchUploadMissions,
        axiosUploadController,
      });

      const apiVariables = {
        params: {
          organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        },
        data: {
          bucketname: bucketName,
          objectname: objectKey,
          uploadId: uploadId,
          parts: parts,
        },
      };
      await api.post('/api/issues/complete-multipart-upload', apiVariables);
    } else {
      await uploadFile({
        file,
        presignedS3Url,
        batchTotalBytes,
        missionId,
        controllerUploadMissions,
        dispatchUploadMissions,
        axiosUploadController,
      });
    }
  } catch (error) {
    // if cancelled, error = {message: 'canceled', name: 'CanceledError', code: 'ERR_CANCELED'}
    log.error('ERROR when uploading file to S3 bucket: ', error);
    consoleLogApiError(error);
    throw error;
  }

  return { Bucket: bucketName, Key: objectKey };
};
